import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './RaffleSection.css';

/**
 * RaffleSection — veckolotteriet överst på butikssidan.
 *
 * Visar öppna lotterier (prisskin, lottpris, nedräkning till dragning,
 * dina lotter och vinstchans) med lottköp, plus de senast avgjorda med
 * vinnare. Dragningen sker automatiskt i backend (job_queue-ticken) vid
 * utsatt tid; vinnaren får skinnet via Steam-trade och kan hämta om
 * leveransen härifrån om den väntar.
 *
 * @param {{onBought?: function}} props Callback efter lyckat köp (för
 *   saldouppdatering på butikssidan).
 * @returns {JSX.Element|null} Lotterisektionen, eller null utan lotterier.
 */
export default function RaffleSection({ onBought }) {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [buyCounts, setBuyCounts] = useState({});
  const [buyingId, setBuyingId] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/shop/raffles`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setData(body); })
      .catch(() => {
        // tyst — lotteriet är icke-kritiskt för sidan
      });
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!data || ((data.open || []).length === 0 && (data.recent || []).length === 0)) {
    return null;
  }

  /**
   * Köp valt antal lotter i ett lotteri.
   *
   * @param {object} raffle Lotteriet att köpa i.
   */
  const buy = async (raffle) => {
    const count = Number(buyCounts[raffle.id] || 1);
    setBuyingId(raffle.id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/shop/raffles/${raffle.id}/buy`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ count }),
      });
      const body = await res.json();
      if (!res.ok) {
        const key = {
          insufficient_balance: 'raffle.errInsufficient',
          raffle_closed: 'raffle.errClosed',
          max_tickets: 'raffle.errMaxTickets',
        }[body.code];
        setMessage({ kind: 'error', text: key ? t(key) : (body.error || t('shop.errGeneric')) });
      } else {
        setMessage({ kind: 'success', text: t('raffle.bought', { count: body.bought }) });
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
        if (onBought) onBought();
        load();
      }
    } catch {
      setMessage({ kind: 'error', text: t('shop.errGeneric') });
    } finally {
      setBuyingId(null);
    }
  };

  /**
   * Hämta (om) leveransen av en vunnen lotterivinst.
   *
   * @param {object} raffle Det dragna lotteriet.
   */
  const claim = async (raffle) => {
    setClaimingId(raffle.id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/shop/raffles/${raffle.id}/claim`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const body = await res.json();
      if (!res.ok) {
        const text = body.code === 'no_trade_url' ? t('shop.errNoTradeUrl') : (body.error || t('shop.errGeneric'));
        setMessage({ kind: 'error', text });
      } else {
        setMessage({ kind: 'success', text: t('shop.claimSent') });
        load();
      }
    } catch {
      setMessage({ kind: 'error', text: t('shop.errGeneric') });
    } finally {
      setClaimingId(null);
    }
  };

  /**
   * Mänsklig nedräkning till dragningen ("2d 4h", "35 min", "snart").
   *
   * @param {string} iso Dragningstid (ISO).
   * @returns {string} Nedräkningstext.
   */
  const countdown = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return t('raffle.drawingSoon');
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return t('raffle.drawsInMinutes', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 48) return t('raffle.drawsInHours', { count: hours });
    return t('raffle.drawsInDays', { count: Math.floor(hours / 24) });
  };

  const isWinner = (raffle) => Boolean(
    user?.steam_username && raffle.winner_name === user.steam_username,
  );

  return (
    <div className="raffle-section">
      <h2 className="raffle-title">🎟️ {t('raffle.title')}</h2>
      {message && <div className={`shop-message ${message.kind}`}>{message.text}</div>}

      {(data.open || []).map((raffle) => {
        const odds = raffle.total_tickets > 0 && raffle.my_tickets > 0
          ? Math.round((raffle.my_tickets / raffle.total_tickets) * 100)
          : null;
        return (
          <div key={raffle.id} className="raffle-card">
            {raffle.prize.image_url ? (
              <img
                className="raffle-prize-image"
                src={raffle.prize.image_url}
                alt={raffle.prize.display_name}
                loading="lazy"
              />
            ) : (
              <div className="raffle-prize-image placeholder">🎁</div>
            )}
            <div className="raffle-info">
              <span className="raffle-prize-name">
                {raffle.prize.stattrak && <span className="shop-stattrak">StatTrak™ </span>}
                {raffle.prize.name}
                <span className="raffle-prize-condition">{raffle.prize.condition}</span>
              </span>
              <span className="raffle-meta">
                🪙 {raffle.ticket_price} {t('raffle.perTicket')}
                {' · '}⏳ {countdown(raffle.draws_at)}
                {' · '}{t('raffle.totalTickets', { count: raffle.total_tickets })}
                {raffle.max_tickets_per_player && (
                  <> · {t('raffle.maxPerPlayer', { count: raffle.max_tickets_per_player })}</>
                )}
              </span>
              {raffle.my_tickets > 0 && (
                <span className="raffle-my-tickets">
                  {t('raffle.myTickets', { count: raffle.my_tickets })}
                  {odds !== null && ` (~${odds}%)`}
                </span>
              )}
            </div>
            {user ? (
              <div className="raffle-buy">
                <input
                  className="raffle-count-input"
                  type="number"
                  min="1"
                  max="50"
                  value={buyCounts[raffle.id] || 1}
                  onChange={(e) => setBuyCounts((prev) => ({ ...prev, [raffle.id]: e.target.value }))}
                />
                <button
                  type="button"
                  className="raffle-buy-btn"
                  disabled={buyingId === raffle.id}
                  onClick={() => buy(raffle)}
                >
                  {buyingId === raffle.id ? t('raffle.buying') : t('raffle.buyBtn')}
                </button>
              </div>
            ) : (
              <span className="shop-login-hint">{t('shop.loginToBuy')}</span>
            )}
          </div>
        );
      })}

      {(data.recent || []).length > 0 && (
        <div className="raffle-recent">
          {(data.recent || []).map((raffle) => (
            <div key={raffle.id} className="raffle-recent-row">
              <span className="raffle-recent-prize">{raffle.prize.display_name}</span>
              {raffle.status === 'drawn' ? (
                <span className="raffle-recent-winner">
                  🏆 {raffle.winner_name}
                </span>
              ) : (
                <span className="raffle-recent-cancelled">{t('raffle.cancelled')}</span>
              )}
              {isWinner(raffle)
                && ['pending', 'failed', 'expired', 'declined'].includes(raffle.trade_status) && (
                <button
                  type="button"
                  className="shop-claim-btn"
                  disabled={claimingId === raffle.id}
                  onClick={() => claim(raffle)}
                >
                  {claimingId === raffle.id ? t('shop.claiming') : t('shop.claim')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
